import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { api, formatBytes, formatDate } from '../lib/api';
import { Layout } from '../components/Layout';
import { 
  FileText, 
  Folder, 
  HardDrive, 
  Pulse,
  ArrowRight,
  CloudArrowUp
} from '@phosphor-icons/react';

export const DashboardPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, docsRes] = await Promise.all([
          api.getDashboardStats(),
          api.getRecentDocuments(5)
        ]);
        setStats(statsRes.data);
        setRecentDocs(docsRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const localeMap = { it: 'it-CH', de: 'de-CH', fr: 'fr-CH', en: 'en-GB' };

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title" data-testid="dashboard-title">
          {t('dashboard.welcome')}, {user?.first_name}
        </h1>
        <p className="text-sm text-[#52525B] mt-1">{user?.client_name}</p>
      </div>

      <div className="content-area">
        {/* Stats Grid */}
        <div className="stats-grid mb-8" data-testid="stats-grid">
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{stats?.document_count || 0}</p>
                <p className="stat-label">{t('dashboard.totalDocuments')}</p>
              </div>
              <div className="w-12 h-12 bg-[#E3F2FD] flex items-center justify-center">
                <FileText size={24} className="text-[#002FA7]" />
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{stats?.folder_count || 0}</p>
                <p className="stat-label">{t('dashboard.totalFolders')}</p>
              </div>
              <div className="w-12 h-12 bg-[#FFF3E0] flex items-center justify-center">
                <Folder size={24} className="text-[#F5B041]" />
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{formatBytes(stats?.storage_used || 0)}</p>
                <p className="stat-label">{t('dashboard.storageUsed')}</p>
              </div>
              <div className="w-12 h-12 bg-[#E8F5E9] flex items-center justify-center">
                <HardDrive size={24} className="text-[#0B8043]" />
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{stats?.recent_activity || 0}</p>
                <p className="stat-label">{t('dashboard.recentActivity')}</p>
              </div>
              <div className="w-12 h-12 bg-[#F3E5F5] flex items-center justify-center">
                <Pulse size={24} className="text-[#7B1FA2]" />
              </div>
            </div>
          </div>
        </div>

        {/* Recent Documents */}
        <div className="bg-white border border-[#E4E4E7]">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E4E7]">
            <h2 className="text-lg font-semibold text-[#09090B]">
              {t('dashboard.recentDocuments')}
            </h2>
            <button 
              onClick={() => navigate('/documents')}
              className="flex items-center gap-2 text-sm text-[#002FA7] hover:text-[#002075] font-medium"
              data-testid="view-all-docs"
            >
              {t('nav.documents')}
              <ArrowRight size={16} />
            </button>
          </div>

          {loading ? (
            <div className="p-8 text-center text-[#52525B]">
              {t('common.loading')}
            </div>
          ) : recentDocs.length === 0 ? (
            <div className="empty-state py-12">
              <CloudArrowUp className="empty-state-icon" size={48} />
              <p className="empty-state-title">{t('dashboard.noDocuments')}</p>
              <p className="empty-state-text">{t('dashboard.uploadFirst')}</p>
              <button
                onClick={() => navigate('/documents')}
                className="mt-4 px-6 py-2 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#002075] transition-colors"
                data-testid="upload-first-doc"
              >
                {t('documents.upload')}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-[#E4E4E7]">
              {recentDocs.map((doc) => (
                <div 
                  key={doc.id} 
                  className="flex items-center justify-between px-6 py-4 hover:bg-[#F4F4F5] transition-colors cursor-pointer"
                  onClick={() => navigate('/documents')}
                  data-testid={`recent-doc-${doc.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="document-icon">
                      <FileText size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#09090B]">{doc.original_filename}</p>
                      <p className="text-xs text-[#52525B]">
                        {formatBytes(doc.size)} • v{doc.version}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#52525B]">
                      {formatDate(doc.created_at, localeMap[language])}
                    </p>
                    <p className="hash-display text-[10px] mt-1 max-w-[200px] truncate">
                      {doc.hash_sha256.substring(0, 16)}...
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
